import {
  breakpointSizes,
  getBreakpointUp,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React, { Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import Box from 'src/components/Box/Box'
import { createSetIsTooltipPopupOpen } from 'src/redux/actions/app.actions'
import { css, CSSProp } from 'styled-components'

interface HoverableSpotlightProps {
  className?: string
  iconSrc: React.ReactElement | string
  onHover?: () => void
  tooltip?: boolean
  tooltipDown?: boolean
  tooltipRight?: boolean
  tooltipLeft?: boolean
  tooltipCenter?: boolean
  tooltipWidth?: number
}

const HoverableSpotlight: React.FC<
  React.PropsWithChildren<HoverableSpotlightProps>
> = (props): React.ReactElement => {
  const [isHover, setIsHover] = useState<boolean>(false)

  const dispatch = useDispatch()

  const tooltipStyling: CSSProp = css`
    position: absolute;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
  `

  return (
    <div
      className={props.className}
      css={css`
        position: relative;
        display: flex;
        align-items: center;
        z-index: 1;
      `}
      onMouseEnter={(): void => {
        if (props.onHover !== undefined) {
          props.onHover()
        }
        setIsHover(true)
      }}
      onMouseLeave={(): void => setIsHover(false)}
    >
      <div
        css={css`
          cursor: pointer;
        `}
      >
        <div
          onClick={(): void => {
            dispatch(createSetIsTooltipPopupOpen(true, props.children))
          }}
          onKeyPress={(): void => {
            dispatch(createSetIsTooltipPopupOpen(true, props.children))
          }}
          tabIndex={0}
          role={'button'}
          css={css`
            ${getBreakpointUp(breakpointSizes.SM)(css`
              pointer-events: none;
            `)}
          `}
        >
          {typeof props.iconSrc === 'string' ? (
            <Fragment>
              <img
                src={props.iconSrc}
                alt={''}
                css={css`
                  width: 16px;

                  ${getBreakpointUp(breakpointSizes.SM)(css`
                    ${isHover &&
                    css`
                      display: none;
                    `}
                  `)}
                `}
              />
              <img
                src={props.iconSrc}
                alt={''}
                css={css`
                  width: 16px;
                  display: none;

                  ${getBreakpointUp(breakpointSizes.SM)(css`
                    ${isHover &&
                    css`
                      display: inline;
                    `}
                  `)}
                `}
              />
            </Fragment>
          ) : (
            props.iconSrc
          )}
        </div>
      </div>
      <Box
        className={props.className}
        noPadding
        css={css`
          background-color: ${styleConstants.colors.blue_dark};
          transition: box-shadow ${styleConstants.transitionSpeed.fastest}
            ease-out;
          padding: ${styleConstants.heights.minorInternalGutter}
            ${styleConstants.widths.cardPaddingXXSMOnly};

          ${!isHover &&
          css`
            pointer-events: none;
            cursor: default;
          `}

          &:hover {
            box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.15);
          }

          opacity: 0;
          position: absolute;
          transition:
            opacity 150ms ease-out,
            left 150ms ease-out,
            right 150ms ease-out;
          overflow: visible;
          width: max-content;
          top: 150%;
          left: 50%;
          transform: translateX(-50%);

          padding: 8px 12px;

          ${getBreakpointUp(breakpointSizes.SM)(css`
            ${isHover &&
            css`
              opacity: 1;
            `}
          `)}
        `}
      >
        {props.tooltip && (
          <div
            className={'tooltip'}
            css={css`
              ${tooltipStyling}
              top: -10px;
              left: 25px;
              border-bottom: 10px solid ${styleConstants.colors.blue_dark};
            `}
          />
        )}
        {props.tooltipDown && (
          <div
            className={'tooltipDown'}
            css={css`
              ${tooltipStyling}
              bottom: -10px;
              left: 25px;
              border-top: 10px solid ${styleConstants.colors.blue_dark};
            `}
          />
        )}
        {props.tooltipRight && (
          <div
            className={'tooltipRight'}
            css={css`
              ${tooltipStyling}
              top: -10px;
              right: 25px;
              border-bottom: 10px solid ${styleConstants.colors.blue_dark};
            `}
          />
        )}
        {props.tooltipLeft && (
          <div
            className={'tooltipLeft'}
            css={css`
              ${tooltipStyling}
              top: -10px;
              left: 25px;
              border-bottom: 10px solid ${styleConstants.colors.blue_dark};
            `}
          />
        )}
        {props.tooltipCenter && (
          <div
            className={'tooltipCenter'}
            css={css`
              ${tooltipStyling}
              top: -10px;
              ${props.tooltipWidth !== undefined &&
              css`
                right: calc(50% - ${props.tooltipWidth / 2}px);
                left: calc(50% - ${props.tooltipWidth / 2}px);
              `}
              border-bottom: 10px solid ${styleConstants.colors.blue_dark};
            `}
          />
        )}
        <p
          css={css`
            ${getFontCss(
              10,
              styleConstants.colors.white,
              styleConstants.fontFamilies.medium,
            )};
            display: flex;
            flex-direction: column;
            overflow: hidden;
            word-wrap: break-word;
            position: relative;
            align-items: center;
          `}
        >
          {props.children}
        </p>
      </Box>
    </div>
  )
}

export default HoverableSpotlight
